/* Bundle deals — staggered card reveal + "biggest saving first" sort.
   Progressive enhancement only: cards are fully visible and in
   collection order until this element connects; the sort button
   ships [hidden] and is only surfaced here, so a JS failure never
   strands a dead control. Pairs with assets/section-bundle-deals.css
   (.bd namespace). */

class BundleDeals extends HTMLElement {
  connectedCallback() {
    this._setupSort();
    this._setupReveal();
    this._setupThumbnailPopovers();
  }

  disconnectedCallback() {
    if (this._io) this._io.disconnect();
    this.classList.remove('bd--reveal');
    if (this._popHide) {
      window.removeEventListener('scroll', this._popHide);
      window.removeEventListener('resize', this._popHide);
    }
    if (this._pop && this._pop.parentNode) {
      this._pop.parentNode.removeChild(this._pop);
      this._pop = null;
    }
  }

  _setupSort() {
    const button = this.querySelector('[data-bd-sort]');
    const grid = this.querySelector('.bd__grid');
    if (!button || !grid) return;

    const cards = Array.from(grid.querySelectorAll('.bd__card'));
    if (cards.length < 2) return;
    const collectionOrder = cards.slice();

    button.hidden = false;
    button.addEventListener('click', () => {
      const pressed = button.getAttribute('aria-pressed') === 'true';
      button.setAttribute('aria-pressed', String(!pressed));

      if (pressed) {
        collectionOrder.forEach((card) => grid.appendChild(card));
        return;
      }
      // Sold-out bundles sink below live deals whatever they save.
      const key = (card) =>
        card.classList.contains('bd__card--soldout') ? -1 : Number(card.dataset.save) || 0;
      cards
        .slice()
        .sort((a, b) => key(b) - key(a))
        .forEach((card) => grid.appendChild(card));
    });
  }

  _setupReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    this.classList.add('bd--reveal');

    this._io = new IntersectionObserver(
      (entries) => {
        // Batch-relative stagger: each viewport entry waves in from 0,
        // so late rows never inherit a 30-card-deep delay.
        let step = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.style.setProperty('--bd-d', `${Math.min(step, 6) * 60}ms`);
          entry.target.classList.add('is-in');
          this._io.unobserve(entry.target);
          step += 1;
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );

    this.querySelectorAll('.bd__hero, .bd__card').forEach((el) => this._io.observe(el));
  }

  // Hover popover for component thumbnails. Modeled on the shoppable svc__tip:
  // a single <body>-appended card positioned from each thumb's screen rect, so
  // it escapes the card's overflow clip and stays viewport-clamped. Desktop
  // (hover-capable) only — on touch the thumbnail just links to the product.
  _setupThumbnailPopovers() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    const thumbs = Array.from(this.querySelectorAll('[data-bd-thumb]'));
    if (!thumbs.length) return;

    const pop = document.createElement('div');
    pop.className = 'bd__pop';
    pop.setAttribute('aria-hidden', 'true');
    document.body.appendChild(pop);
    this._pop = pop;

    this._hidePopover = this._hidePopover.bind(this);
    this._popHide = () => this._hidePopover();

    thumbs.forEach((thumb) => {
      thumb.addEventListener('mouseenter', () => this._showPopover(thumb));
      thumb.addEventListener('focus', () => this._showPopover(thumb));
      thumb.addEventListener('mouseleave', this._hidePopover);
      thumb.addEventListener('blur', this._hidePopover);
      // The thumbnail is a quick-view trigger (quick-view-modal.js); drop the
      // hover popover the moment it's clicked so it can't sit over the modal.
      thumb.addEventListener('click', this._hidePopover);
    });
    window.addEventListener('scroll', this._popHide, { passive: true });
    window.addEventListener('resize', this._popHide);
  }

  _showPopover(thumb) {
    const pop = this._pop;
    if (!pop) return;
    const tpl = thumb.parentElement && thumb.parentElement.querySelector('template.bd__pop-src');
    if (!tpl) return;
    pop.replaceChildren(tpl.content.cloneNode(true));
    pop.classList.add('is-visible'); // reveal first so offsetWidth is measurable

    const r = thumb.getBoundingClientRect();
    const margin = 10;
    const half = pop.offsetWidth / 2;
    const center = r.left + r.width / 2;
    const clampedX = Math.min(Math.max(center, margin + half), window.innerWidth - margin - half);
    pop.style.left = `${clampedX}px`;
    pop.style.top = `${r.top - 12}px`; // CSS lifts it up by its own height (sits above the thumb)

    // Keep the caret under the thumb's true centre even when the card is clamped.
    const caretX = Math.min(Math.max(center - (clampedX - half), 16), half * 2 - 16);
    pop.style.setProperty('--bd-pop-caret-x', `${caretX}px`);
  }

  _hidePopover() {
    if (this._pop) this._pop.classList.remove('is-visible');
  }
}

if (!customElements.get('bundle-deals')) {
  customElements.define('bundle-deals', BundleDeals);
}
